import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import defaultBrand from './brand-default.scss';
import createAppTheme from './theme';
import { BookingConfigEnum } from '../components/BookingWidget/bookingSteps.interface';
import { useBookingConfigContext } from '../context/bookingConfigContext/bookingConfigContext';
import { CacheDataContext } from '../context/cacheContext/cacheContext';
import { useSessionContext } from '../context/sessionContext/sessionContext';
import {
  applyVisualCssVariables,
  getVisualValues,
  getVisualVariant,
  VisualValues,
} from '../shared/helpers/visualVariant';

const getVariantBrandColors = (
  brandColors: any,
  visualValues: VisualValues,
): any => ({
  ...brandColors,
  primary: visualValues.primaryColor,
  secondary: visualValues.secondaryColor,
  secondaryLight: visualValues.secondaryLightColor,
  darkPrimary: visualValues.darkPrimaryColor,
  darkSecondary: visualValues.darkSecondaryColor,
  iconAvailable: visualValues.iconAvailableColor,
  iconNotAvailable: visualValues.iconNotAvailableColor,
});

const setStyleColors = (brandColors: any): void => {
  const root = document.documentElement;

  if (brandColors.primary)
    root.style.setProperty('--primary', brandColors.primary);
  if (brandColors.secondaryLight)
    root.style.setProperty('--secondary-light', brandColors.secondaryLight);
  if (brandColors.textPrimary)
    root.style.setProperty('--text-primary', brandColors.textPrimary);
  if (brandColors.darkPrimary)
    root.style.setProperty('--dark-primary', brandColors.darkPrimary);
  if (brandColors.darkMiddle)
    root.style.setProperty('--dark-middle', brandColors.darkMiddle);
  if (brandColors.darkSecondary)
    root.style.setProperty('--dark-secondary', brandColors.darkSecondary);
  if (brandColors.lightPrimary)
    root.style.setProperty('--light-primary', brandColors.lightPrimary);
  if (brandColors.lightSecondary)
    root.style.setProperty('--light-secondary', brandColors.lightSecondary);
  if (brandColors.contrastPrimary)
    root.style.setProperty('--contrast-primary', brandColors.contrastPrimary);
  if (brandColors.selectedCard)
    root.style.setProperty('--selected-card', brandColors.selectedCard);
  if (brandColors.secondary)
    root.style.setProperty('--secondary', brandColors.secondary);
  if (brandColors.rdp)
    root.style.setProperty('--red-dark-primary', brandColors.rdp);
  if (brandColors.contrastSecondary)
    root.style.setProperty(
      '--contrast-secondary',
      brandColors.contrastSecondary,
    );
  if (brandColors.iconAvailable)
    root.style.setProperty('--icon-available', brandColors.iconAvailable);
  if (brandColors.iconNotAvailable)
    root.style.setProperty(
      '--icon-not-available',
      brandColors.iconNotAvailable,
    );
  if (brandColors.sectionTitleColor)
    root.style.setProperty(
      '--section-title-color',
      brandColors.sectionTitleColor,
    );
  if (brandColors.textBlack)
    root.style.setProperty('--text-black', brandColors.textBlack);
  if (brandColors.linkButtonColor)
    root.style.setProperty('--link-button-color', brandColors.linkButtonColor);
  if (brandColors.backButtonBg)
    root.style.setProperty('--back-button-bg', brandColors.backButtonBg);
  if (brandColors.backButtonColor)
    root.style.setProperty('--back-button-color', brandColors.backButtonColor);
};

const ConfiguredThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { bookingConfigData } = useBookingConfigContext();
  const { getUrlParam } = useContext(CacheDataContext);
  const { decodedSessionToken } = useSessionContext();
  const brandHint =
    bookingConfigData[BookingConfigEnum.Brand] || getUrlParam('brand') || '';
  const visualVariant = getVisualVariant(
    bookingConfigData,
    brandHint,
    Boolean(decodedSessionToken?.agentId),
  );
  const visualValues = useMemo(
    () => getVisualValues(visualVariant),
    [visualVariant],
  );
  const brandColors = useMemo(
    () => getVariantBrandColors(defaultBrand, visualValues),
    [visualValues],
  );
  const theme = useMemo(
    () => createAppTheme(brandColors, '', visualValues),
    [brandColors, visualValues],
  );

  useEffect(() => {
    setStyleColors(brandColors);
    applyVisualCssVariables(visualValues);
    document.documentElement.setAttribute('data-visual-variant', visualVariant);
  }, [brandColors, visualValues, visualVariant]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
};

export default ConfiguredThemeProvider;
